/**
 * CetusEd NextGen — auth transport primitives.
 *
 * The four password-auth endpoints live at /api/auth/* (packages/server/src/
 * routes/auth.ts), NOT under /api/v1, so they are spoken here with plain fetch
 * rather than through the /api/v1-rooted hono `hc` client — whose /api/v1/auth/*
 * routes are notImplemented() stubs. See ./contract.ts for the full note.
 *
 * These are PURE transport: input → parsed output, non-2xx throws (unwrap). They
 * hold no session state and perform no refresh-on-401 retry — that orchestration
 * belongs to core/session/session-controller.ts, which composes these. Keeping
 * them stateless is what makes the login vertical testable without a store.
 *
 * ⛔2 — a thrown AppError from unwrap is the ONLY success signal. There is no
 * fabricated "logged in": a 401/423/429 throws and never resolves.
 */
import { API_BASE } from './config'
import { unwrap } from './response'
import type {
  LoginInput,
  AuthLoginResponse,
  AuthRefreshResponse,
  AuthLogoutResponse,
} from './contract'

const JSON_HEADERS = { 'Content-Type': 'application/json' } as const

function bearer(accessToken: string | null): Record<string, string> {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
}

/** POST /api/auth/login — credential presentation. No bearer; the tenant is host-resolved. */
export async function postLogin(input: LoginInput): Promise<AuthLoginResponse> {
  const res = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  })
  return unwrap<AuthLoginResponse>(res)
}

/**
 * POST /api/auth/refresh — rotate the presented refresh token for a new pair.
 * The access token is sent as a bearer when present: the endpoint currently
 * sits BEHIND authMiddleware (TASK-00082), so a refresh-token-only call is
 * rejected until that route is made public. Sending the (possibly expired)
 * access token is the honest best effort; when it is rejected the caller fails
 * closed. Attaching it costs nothing and lets post-expiry renewal start working
 * the day TASK-00082 lands.
 */
export async function postRefresh(
  refreshToken: string,
  accessToken: string | null,
): Promise<AuthRefreshResponse> {
  const res = await fetch(`${API_BASE}/api/auth/refresh`, {
    method: 'POST',
    headers: { ...JSON_HEADERS, ...bearer(accessToken) },
    body: JSON.stringify({ refreshToken }),
  })
  return unwrap<AuthRefreshResponse>(res)
}

/**
 * POST /api/auth/logout — revoke the presented refresh token server-side.
 * Also behind authMiddleware today (TASK-00082); when it 401s the caller still
 * clears the local session (see session-controller.signOut). The response is
 * HONEST about whether a live token was revoked (revoked:true/false).
 */
export async function postLogout(
  refreshToken: string,
  accessToken: string | null,
): Promise<AuthLogoutResponse> {
  const res = await fetch(`${API_BASE}/api/auth/logout`, {
    method: 'POST',
    headers: { ...JSON_HEADERS, ...bearer(accessToken) },
    body: JSON.stringify({ refreshToken }),
  })
  return unwrap<AuthLogoutResponse>(res)
}
